<script lang="ts">
  import Router, { push, type ConditionsFailedEvent, location } from "svelte-spa-router";
  import { wrap } from "svelte-spa-router/wrap";
  import { onDestroy, onMount } from "svelte";
  import Login from "./routes/Login.svelte";
  import { LoadingSpinner } from "$ui/loading-spinner";
  import { Toaster } from "$ui/sonner";
  import { applyTheme } from "$lib/utils/helpers";
  import SetupOrganisation from "./routes/SetupOrganisation.svelte";
  import OrgAdminConsent from "./routes/OrgAdminConsent.svelte";
  import { authLoaded, authStateStore, type AuthState } from "$lib/stores/user";
  import { envLoaded, sessionId } from "$lib/utils/env";
  import CreateTeam from "./routes/CreateTeam.svelte";
  import UserSidebarPage from "./routes/UserSidebarPage.svelte";
  import EnvironmentBanner from "$lib/components/layout/EnvironmentBanner.svelte";
  import { derived } from "svelte/store";
  import { fullPageRedirectState } from "$lib/utils/fullPageRedirects";
  import { selectedOrgIdStore } from "$lib/stores/orgs";
  import { setAnalyticsIdentity } from "$lib/utils/analytics";

  async function envAndAuthLoaded() {
    await envLoaded;
    await authLoaded;
  }

  let currentAuthState: AuthState = { loading: true };
  const authStateUnsub = authStateStore.subscribe((authState) => {
    currentAuthState = authState;
  });

  // Collect analytics on page change
  derived([authStateStore, location, selectedOrgIdStore], (both) => both).subscribe(([authState, loc, orgId]) => {
    if (authState.userInfo) {
      setAnalyticsIdentity(authState.userInfo, sessionId + "1", loc, orgId);
    }
  });

  async function redirectToLogin(event: ConditionsFailedEvent) {
    console.error("Route condition failed, redirecting to /login");
    await push("/login");
  }

  function userIsAuthed(): boolean {
    return !currentAuthState.loading && currentAuthState.userInfo !== undefined;
  }

  onDestroy(() => {
    authStateUnsub();
  });

  // Only the root spa-router handles param parsing, so we duplicate entries here to ensure params are parsed
  const globalRoutes = {
    // --------- General Routes ---------
    "/login": Login,
    "/create-team": wrap({
      component: CreateTeam,
      conditions: [userIsAuthed],
    }),
    "/admin-consent": OrgAdminConsent,
    "/create-org": wrap({
      component: SetupOrganisation,
      conditions: [userIsAuthed],
    }),

    // --------- Team Routes ---------
    "/t/:teamId": wrap({
      component: UserSidebarPage,
      conditions: [userIsAuthed],
    }),
    "/t/:teamId/*": wrap({
      component: UserSidebarPage,
      conditions: [userIsAuthed],
    }),

    // --------- Org Routes ---------
    "/o/:orgId": wrap({
      component: UserSidebarPage,
      conditions: [userIsAuthed],
    }),
    "/o/:orgId/*": wrap({
      component: UserSidebarPage,
      conditions: [userIsAuthed],
    }),

    // --------- Onboarding Routes ---------
    "/s/:setupId": wrap({
      component: UserSidebarPage,
      conditions: [userIsAuthed],
    }),
    "/s/:setupId/": wrap({
      component: UserSidebarPage,
      conditions: [userIsAuthed],
    }),

    "*": wrap({
      component: UserSidebarPage,
      conditions: [userIsAuthed],
    }),
  };

  // TODO: Refactor to use theme store
  let selectedMode = "light";

  onMount(async () => {
    applyTheme(selectedMode);

    const redirectState = await fullPageRedirectState;
    console.log("fullPageRedirectState", redirectState);
    if (redirectState.redirectTo) {
      push(redirectState.redirectTo);
    }
  });
</script>

<EnvironmentBanner>
  <div class="bg-offwhite dark:bg-background-dark h-full">
    {#await envAndAuthLoaded()}
      <div class="flex items-center justify-center h-full">
        <LoadingSpinner />
      </div>
    {:then}
      <Router routes={globalRoutes} on:conditionsFailed={redirectToLogin} on:routeLoaded={console.debug} />
    {/await}
  </div>
</EnvironmentBanner>

<Toaster />
